import { alertController, createGesture, Gesture } from "@ionic/vue";
import { Directive, DirectiveBinding } from "vue";
import router, { route } from "@/router";

import { PartiallyRequired } from "@/types/helper";

// Move to types folder
export interface RouteBackGuardDirectiveBinding {
    interrupt: (value?: any) => boolean;
    handler: (value?: any) => void;
    value?: any;
    modifiers?: {
        rebindOnUpdate: boolean;
    }
}

type ValueRequiredDirectiveBinding<T> = PartiallyRequired<Partial<DirectiveBinding<T>>, 'value'>

let currentGuardBackDirectiveGesture: Gesture | undefined

let listenerFunction: (ev: Event) => void;

const findActivatable = (target: HTMLElement): number | NodeJS.Timeout | undefined => {
    if (target.classList.contains('ion-activatable')) {
        target.classList.add('ion-activated')
        return setTimeout(() => {
            target.classList.remove('ion-activated')
        }, 200)
    }

    if (!target.parentElement || target.tagName === 'ion-app') return
    findActivatable(target.parentElement)
}

const CustomDisableSwipeBackDirective = {
    beforeMount(el: Node) {
        const gesture = createGesture({
            el,
            threshold: 0,
            gestureName: 'goback-swipe',
            gesturePriority: 40.5,
            onStart: (event) => {
                if (event.deltaX + event.deltaY === 0) {
                    if (!event.event.target) return
                    findActivatable(event.event.target as HTMLElement)
                }
            }
        });
        gesture.enable(true);
    }
};

export const handleBackEvent = (ev: Event, binding: ValueRequiredDirectiveBinding<RouteBackGuardDirectiveBinding>): void => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    ev.detail.register(100, async (processNextHandler) => {
        if (route.value.meta?.disableBack) return
        if (binding.value.interrupt()) return binding.value.handler()

        processNextHandler()
    })
}

const handleBackGesture = async (binding: ValueRequiredDirectiveBinding<RouteBackGuardDirectiveBinding>): Promise<void> => {
    const alert = await alertController.getTop()
    if (alert) return
    if (!binding.value.interrupt()) return

    binding.value.handler()
}

export const destroyCurrentGuardBack = (): void => {
    currentGuardBackDirectiveGesture?.destroy()
}

export const createBackGuardGesture = (el: Node, binding: ValueRequiredDirectiveBinding<RouteBackGuardDirectiveBinding>): void => {
    currentGuardBackDirectiveGesture = createGesture({
        el,
        threshold: 0,
        gestureName: 'goback-swipe',
        gesturePriority: 40.5,
        onMove: (detail) => {
            if (detail.startX < 35 && detail.deltaX > 100) handleBackGesture(binding)
        },
        onEnd: (detail) => {
            if (binding.value.interrupt()) return
            if (detail.startX < 35 && detail.deltaX > 100) router.go(-1)
        }
    })
    currentGuardBackDirectiveGesture.enable(true)
}

const CustomRouterGuardBackDirective: Directive = {
    beforeMount(el: Node, binding: DirectiveBinding<RouteBackGuardDirectiveBinding>): void {
        createBackGuardGesture(el, binding)

        listenerFunction = (ev: Event) => handleBackEvent(ev, binding)

        document.addEventListener('ionBackButton', listenerFunction)
    },
    updated(el: Node, binding: DirectiveBinding<RouteBackGuardDirectiveBinding>): void {
        if (binding.modifiers?.rebindOnUpdate) {
            currentGuardBackDirectiveGesture?.destroy()
            createBackGuardGesture(el, binding)
        }
    },
    beforeUnmount(): void {
        currentGuardBackDirectiveGesture?.destroy()
        document.removeEventListener('ionBackButton', listenerFunction)
    }
}

const IonContentScrollbarDirective: Directive<HTMLElement> = {
    mounted(el: HTMLElement): void {
        const scrollbarStyle = `
        ::-webkit-scrollbar {
            width: 3px;
            height: 0;
        }
        ::-webkit-scrollbar-thumb {
            background: var(--scrollbar-foreground);
            border-radius: 3px;
        }

        ::-webkit-scrollbar-track {
            background: var(--scrollbar-background);
            border-radius: 3px;
        }`

        if (!el.shadowRoot) return

        const shadowStyle = el.shadowRoot?.querySelector('style')

        if (!shadowStyle) {
            const newStyle = document.createElement('style')
            newStyle.append(scrollbarStyle)
            el.shadowRoot.appendChild(newStyle)
            return
        }

        shadowStyle.append(scrollbarStyle)
    }
}

export {
    CustomDisableSwipeBackDirective,
    CustomRouterGuardBackDirective,
    IonContentScrollbarDirective
}
